import { useEffect, useState } from "react"
import getFirebaseInstance from "./firebase"
import useLocalFirestore from "./useLocalFirestore"
import useNotifications from "../notifications/useNotifications"
import useSiteMetrics from "../siteMetrics/useSiteMetrics"
import { hotjar } from "react-hotjar"

function useAuth() {
  const [user, setUser] = useState(null)
  const [firebase, setFirebase] = useState(null)
  const [loading, setLoading] = useState(true)

  // state to track if user is logged out, waiting for auth state to resolve to confirm. Required for proper redirects 
  const [isUserLoggedOut, setIsUserLoggedOut] = useState(null)
  const [profile, setProfile] = useState(null)
  const [globalSiteConfig, setGlobalSiteConfig] = useState(null)
  const [schoolMemberships, setSchoolMemberships] = useState(null)

  const localFirestore = useLocalFirestore(firebase)
  const notifier = useNotifications(firebase, profile)
  const siteMetrics = useSiteMetrics(firebase)

  const updateAssessment = async () => {
    const assessmentRecord = await firebase?.getAssessmentRecord(user.uid)

    if (assessmentRecord) {
      setProfile({ ...profile, assessmentRecord })
    }
  }

  const updateSchoolMemberships = async (firebaseInstance) => {
    const schoolMembershipData = await firebaseInstance.getUserSchoolMemberships()
    if (schoolMembershipData !== null) {
      setSchoolMemberships(schoolMembershipData)
    }
  }

  // Merge SQL school codes into profile.schCodes, this is a temporary solution until we can fully transition to SQL for school memberships. 
  // This ensures that any school codes from SQL are included in the profile for filtering and access control throughout the app.
  useEffect(() => {
    if (profile && schoolMemberships && Array.isArray(schoolMemberships)) {
      const sqlSchoolCodes = schoolMemberships.map((m) => m.sch_code).filter(Boolean)
      const firebaseSchoolCodes = profile.schCodes || []
      const merged = [...new Set([...firebaseSchoolCodes, ...sqlSchoolCodes])]
      
      if (JSON.stringify((profile.schCodes || []).sort()) !== JSON.stringify(merged.sort())) {
        setProfile({ ...profile, schCodes: merged })
      }
    }
  }, [profile?.userId, schoolMemberships])

  useEffect(() => {
    if (user === null && !loading) {
      console.log("Setting isUserLoggedOut to true")
      setIsUserLoggedOut(true)
    } else if (user !== null && !loading) {
      console.log("Setting isUserLoggedOut to false")
      setIsUserLoggedOut(false)
    }
  }, [loading, user])

  useEffect(() => {
    if (!!profile) {
      hotjar.initialize("3484205", "6")

      const { userId } = profile
      if (userId) {
        hotjar.identify(userId)
      }
    }
  }, [profile])

  useEffect(() => {
    let unsubscribe
    let profileUnsubscribe
    let globalSiteConfigUnsubscribe
    let userActivitiesUnsubscribe
    let userNotificationsUnsubscribe
    let userPointTotalUnsubscribe

    const firebaseInstance = getFirebaseInstance()
    setFirebase(firebaseInstance)

    unsubscribe = firebaseInstance.auth.onAuthStateChanged((userResult) => {
      if (userResult) {
        firebaseInstance.loginCount(userResult.uid)
        profileUnsubscribe = firebaseInstance.getUserProfile({
          userId: userResult.uid,
          onSnapshot: async (r) => {
            const userMembership = await firebaseInstance.getUserMembership(userResult.uid)
            const assessmentRecord = await firebaseInstance.getAssessmentRecord(userResult.uid)

            setUser({
              ...userResult,
              email: userResult._delegate.email,
              providerId: userResult._delegate.providerId,
              membership: userMembership,
            })

            setProfile(r.empty ? null : { ...r.data(), assessmentRecord })

            setLoading(false)
          },
        })

        globalSiteConfigUnsubscribe = firebaseInstance.getGlobalSiteConfig({
          onSnapshot: async (r) => {
            const globalSiteConfig = r.data()
            setGlobalSiteConfig(globalSiteConfig)
          },
        })

        userActivitiesUnsubscribe = firebaseInstance.subscribeToUserActivities({
          onSnapshot: async (r) => {
            const object = r.data()
            localFirestore.setActivityRecord(object?.completedActivities || [])
          },
        })

        userNotificationsUnsubscribe = firebaseInstance.subscribeToUserNotifications({
          onSnapshot: async (r) => {
            const object = r.data()

            const notifications = object?.notifications?.filter((notification) => notification.isHidden !== true) || []
            const educatorNotifications = object?.educatorNotifications?.filter((notification) => notification.isHidden !== true) || []

            const scheduledNotifications = object?.scheduledNotifications || []

            // Get the current date
            const currentDate = new Date()

            // Function to convert expiration data to JavaScript Date
            function getDateFromTimestamp(timestamp) {
              if (!timestamp) return null
              const { seconds, nanoseconds } = timestamp
              const milliseconds = seconds * 1000 + nanoseconds / 1000000
              return new Date(milliseconds)
            }

            const sortedNotifications = notifications
              .sort((a, b) => {
                return b.publicationDate - a.publicationDate
              })
              .filter((item) => {
                const expDate = getDateFromTimestamp(item.expirationDate)
                return !expDate || expDate > currentDate // keep if no expiration date or expiration is in the future
              })

            const sortedEducatorNotifications = educatorNotifications
              .sort((a, b) => {
                return b.publicationDate - a.publicationDate
              })
              .filter((item) => {
                const expDate = getDateFromTimestamp(item.expirationDate)
                return !expDate || expDate > currentDate // keep if no expiration date or expiration is in the future
              })

            localFirestore.handleNewNotifications(sortedNotifications || [])
            localFirestore.setScheduledNotificationsRecord(scheduledNotifications || [])
            localFirestore.setEducatorNotificationsRecord(sortedEducatorNotifications || [])
            notifier.setHistoricalNotificationsRecord(object?.notifications || [])
            notifier.setHistoricalEducatorNotificationsRecord(object?.educatorNotifications || [])
          },
        })

        userPointTotalUnsubscribe = firebaseInstance.subscribeToUserPointTotal({
          onSnapshot: async (r) => {
            const object = r.data()
            const pointTotal = object?.pointTotal || null
            localFirestore.setPointTotalRecord(pointTotal)
          },
        })
      } else {
        // When we no longer have a logged in user
        // we cancel the snapshot listener for their profile.
        if (profileUnsubscribe) {
          profileUnsubscribe()
        }
        if (globalSiteConfigUnsubscribe) {
          globalSiteConfigUnsubscribe()
        }

        if (userActivitiesUnsubscribe) {
          userActivitiesUnsubscribe()
        }

        if (userNotificationsUnsubscribe) {
          userNotificationsUnsubscribe()
        }

        if (userPointTotalUnsubscribe) {
          userPointTotalUnsubscribe()
        }

        setUser(null)
        setLoading(false)
        setProfile(null)
      }

      if (firebaseInstance && schoolMemberships === null) {
        updateSchoolMemberships(firebaseInstance)
      }
    })

    return () => {
      // on unmount, cancel firebase snapshot listeners
      if (unsubscribe) {
        unsubscribe()
      }

      if (profileUnsubscribe) {
        profileUnsubscribe()
      }
    }
  }, [])

  return {
    user,
    firebase,
    loading,
    profile,
    globalSiteConfig,
    updateAssessment,
    localFirestore,
    notifier,
    schoolMemberships,
    isUserLoggedOut,
  }
}

export default useAuth
